import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PendingToolApproval } from "../../../api/threads/liveMessages";
import { answerToolApproval, type ToolApprovalChoice, type ToolApprovalOutcome } from "../../../api/threads/toolApprovalsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { shortReference } from "../../../shared/references/shortReferences";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import { outcomeTextKey, vouchOf, type AnswerableRequest } from "./toolApproval";

type Tool = PendingToolApproval["tool"];

/** Who vouches for the version, in one sentence. */
function Vouch({ tool }: { tool: Tool }) {
  const { t } = useTranslation();
  const vouch = vouchOf(tool);
  switch (vouch.kind) {
    case "reviewed":
      return <p className="text-sm">{vouch.author === null ? t("threads.toolApproval.reviewedBy", { reviewers: vouch.reviewers.join(", ") }) : t("threads.toolApproval.byReviewed", { author: vouch.author, reviewers: vouch.reviewers.join(", ") })}</p>;
    case "authorOnly":
      return <p className="text-sm">{t("threads.toolApproval.authorOnly", { author: vouch.author })}</p>;
    case "unknown":
      return <p className="text-sm">{t("threads.toolApproval.unknownVouch")}</p>;
  }
}

/** The version, runtime, dependencies, limits, model and what it reads, as the PC's page shows them: on demand (P-C). */
function ToolDetailRows({ tool }: { tool: Tool }) {
  const { t } = useTranslation();
  const reads = [...tool.workspace_files, ...(tool.reads_sources ? [t("threads.toolApproval.readsSources")] : [])];
  const rows: [string, string][] = [
    [t("threads.toolApproval.detailVersion"), shortReference("version", tool.tool_ref)],
    [t("threads.toolApproval.detailRuntime"), tool.runtime === "python" ? `Python ${tool.requires_python ?? ""}`.trim() : "Node"],
    [t("threads.toolApproval.detailDependencies"), tool.dependencies.length > 0 ? tool.dependencies.join(", ") : t(tool.runtime === "node" ? "threads.toolApproval.nodeOnly" : "threads.toolApproval.dependenciesNone")],
    [
      t("threads.toolApproval.detailLimits"),
      tool.limits === null
        ? t("threads.toolApproval.serverDefaults")
        : t("threads.toolApproval.limits", { seconds: tool.limits.timeout_seconds, memory: tool.limits.memory_megabytes, output: tool.limits.output_megabytes }),
    ],
    ...(tool.model_use === "none" ? [] : [[t("threads.toolApproval.detailModel"), t(tool.model_use === "local_embedding" ? "threads.toolApproval.localEmbedding" : "threads.toolApproval.apiEmbedding")] as [string, string]]),
    ...(reads.length === 0 ? [] : [[t("threads.toolApproval.detailReads"), reads.join(", ")] as [string, string]]),
    // The tool SDK the server sends, when the PC asks about it too (connector consent, T-1 (a)).
    ...(tool.sdk == null ? [] : [[t("threads.toolApproval.detailSdk"), t("threads.toolApproval.sdkNew", { version: tool.sdk.version })] as [string, string]]),
  ];
  return (
    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="app-text-muted">{label}</dt>
          <dd className="break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The host's answer in the Thread to the version their PC waits on (connector.md TA-6): the
 * same Approve and Decline the PC's own page offers, relayed by the server to that PC's
 * connector, which decides; its answer shows here. Leaving it unanswered is the page's Later.
 */
export default function ToolApprovalAnswer({ request, pc }: { request: AnswerableRequest; pc: string }) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  const [answered, setAnswered] = useState<{ readonly outcome: ToolApprovalOutcome; readonly answer: ToolApprovalChoice } | null>(null);
  const tool = request.pending.tool;
  const answer = (choice: ToolApprovalChoice) => {
    setAnswered(null);
    submit(async () => {
      setAnswered({ outcome: await answerToolApproval(request.threadId, request.turnId, request.pending.request_id, choice), answer: choice });
    });
  };
  const busy = state.status === "submitting" || answered?.outcome === "recorded";
  return (
    <div className="space-y-2">
      <Vouch tool={tool} />
      <div className="flex flex-wrap gap-2">
        <AppButton type="button" variant="primary" size="sm" disabled={busy} onClick={() => { answer("approved"); }}>{t("threads.toolApproval.approve")}</AppButton>
        <AppButton type="button" variant="secondary" size="sm" disabled={busy} onClick={() => { answer("denied"); }}>{t("threads.toolApproval.decline")}</AppButton>
      </div>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      <div role="status" aria-live="polite">
        {answered === null ? null : <p className="app-text text-xs">{t(`threads.toolApproval.outcomes.${outcomeTextKey(answered.outcome, answered.answer)}`, { pc })}</p>}
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer">{t("threads.toolApproval.details")}</summary>
        <ToolDetailRows tool={tool} />
      </details>
    </div>
  );
}
